import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ProtectedRoute from "./auth/ProtectedRoute";
import { HosProvider } from "./hos/hosStore";
import NetworkActivity from "./components/NetworkActivity";
import { GlobalRuntimeErrorBanner, RouteErrorBoundary } from "./components/AppErrorBoundary";
import {
  disableLiveTranslationObserver,
  enableLiveTranslationObserver,
  prepareDomAutoTranslation,
  syncTranslationLanguage,
  translateDocumentLive,
  translateDocumentOnce,
} from "./i18n/autoTranslateDom";

const ModuleShell = lazy(() => import("./screens/ModuleShell"));
const SettingsShell = lazy(() => import("./screens/settings/SettingsShell"));
const Login = lazy(() => import("./screens/Auth/Login"));
const Mfa = lazy(() => import("./screens/Auth/Mfa"));
const AuthCallback = lazy(() => import("./screens/Auth/AuthCallback"));
const XentrosDashboardWorkspace = lazy(() =>
  import("./screens/PMS/PMS.Host").then((module) => ({ default: module.XentrosDashboardWorkspace }))
);

function AppLoading() {
  return <div style={{ padding: 20, fontWeight: 900 }}>Loading...</div>;
}

function LegacyIdentityRedirect() {
  const location = useLocation();
  const path = location.pathname;

  if (path.startsWith("/settings/identity/roles") || path.startsWith("/settings/roles")) {
    return <Navigate to="/module/identity/roles" replace />;
  }

  return <Navigate to="/module/identity" replace />;
}

export default function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    prepareDomAutoTranslation(i18n);
    translateDocumentOnce();
    enableLiveTranslationObserver(i18n);
    return () => {
      disableLiveTranslationObserver();
    };
  }, [i18n]);

  useEffect(() => {
    syncTranslationLanguage(i18n);
    translateDocumentOnce();
    translateDocumentLive(i18n);
  }, [i18n.language, i18n.resolvedLanguage]);

  const location = useLocation();

  useEffect(() => {
    translateDocumentOnce();
    translateDocumentLive(i18n);
  }, [location.pathname, location.search]);

  return (
    <HosProvider>
      <NetworkActivity />
      <GlobalRuntimeErrorBanner />
      <RouteErrorBoundary>
        <Suspense fallback={<AppLoading />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/mfa" element={<Mfa />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <XentrosDashboardWorkspace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/identity/*"
              element={
                <ProtectedRoute>
                  <LegacyIdentityRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/roles"
              element={
                <ProtectedRoute>
                  <LegacyIdentityRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/*"
              element={
                <ProtectedRoute>
                  <SettingsShell />
                </ProtectedRoute>
              }
            />

            <Route
              path="/module/:key/*"
              element={
                <ProtectedRoute>
                  <ModuleShell />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    </HosProvider>
  );
}
